import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { BaseServicePrintDialogStore } from './base-service-print-dialog.store';
import { fb } from '../../../../modules/shared/shared.global-service';
import { FilterBuilder } from '../../../../modules/content/helper/filter.builder';
import { ServicePatternType } from '@libs/types/src/services/enum/service-pattern-type.enum';
import { FormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { CheckInputComponent } from '../../../kit/input/check-input/check-input.component';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import {
    contentGlobalErrors,
    contentGlobalSuccess,
} from '../../../../modules/content/content.error';
import { printFile } from '../../../../modules/shared/helpers/file-download.helper';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';
import { baseExportFontSizeEnumOptions } from '@libs/types/src/services/enum/base-export.enum';

export interface BaseServicePrintDialogSettings {
    headerRequired?: boolean;
    footerRequired?: boolean;
    fontSizeEnabled?: boolean;
}

export const basePrintDialogImports = [
    FormsModule,
    TranslatePipe,
    SelectInputComponent,
    CheckInputComponent,
    ActionButtonsComponent,
];

@Component({
    selector: 'app-base-service-print-dialog',
    templateUrl: 'base-service-print-dialog.component.html',
    styleUrl: 'base-service-print-dialog.component.scss',
    providers: [BaseServicePrintDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ...basePrintDialogImports,
    ],
})
/**
 * data must contain
 * @property {number} serviceId - The ID of the service used for printing.
 * @property {(value: any) => Promise<FolderPrintObject>} onPrint - A function that triggers the print process
 * and resolves when printing is complete.
 * @property {(value: any) => Promise<boolean>} onStart - An alternative callback that starts a background print task.
 * @property {BaseServicePrintDialogSettings} [settings] - Optional settings to customize dialog behavior.
 */
export class BaseServicePrintDialogComponent extends BaseDialogComponent {
    public readonly store = inject(BaseServicePrintDialogStore);
    public readonly translate = inject(TranslateService);

    protected form = fb.group({
        archive: fb.control<boolean>(this.dialogConfig.data.archive ?? false),
        headerId: fb.control<number | null>(null, Validators.required),
        footerId: fb.control<number | null>(null, Validators.required),
        fontSize: fb.control<string | null>({ value: null, disabled: true }),
    });

    protected processing: boolean = false;

    private readonly serviceId = this.dialogConfig.data.serviceId;
    private readonly settings: BaseServicePrintDialogSettings | undefined =
        this.dialogConfig.data.settings;

    constructor() {
        super();

        if (this.settings != null) {
            if (!this.settings?.headerRequired) {
                this.form.get('headerId')?.clearValidators();
                this.form.get('headerId')?.updateValueAndValidity();
            }

            if (!this.settings?.footerRequired) {
                this.form.get('footerId')?.clearValidators();
                this.form.get('footerId')?.updateValueAndValidity();
            }

            if (this.settings?.fontSizeEnabled) {
                this.form.get('fontSize')?.enable();
            }
        }

        this.serviceId &&
            this.store.api
                .getServicePatterns(
                    FilterBuilder.singlePage({
                        serviceId: Number(this.serviceId),
                        type: ServicePatternType.DOCUMENT,
                    }),
                )
                .then();
    }

    protected async print(): Promise<void> {
        if (this.processing) return;
        this.processing = true;
        const value = { ...this.form.value, serviceId: this.serviceId };
        try {
            if (this.dialogConfig.data.onStart) {
                const started = await this.dialogConfig.data.onStart(value);
                if (started) this.dialogRef.close();
                return;
            }

            const ret = (await this.dialogConfig.data.onPrint(
                value,
            )) as FolderPrintObject;
            if (!ret) {
                contentGlobalErrors.next('errors.errorOccurred');
                return;
            }

            printFile(ret.pdfBase64, ret.fileName, 'application/pdf');
            contentGlobalSuccess.next('common.successfullyPrinted');
            this.dialogRef.close();
        } catch {
            contentGlobalErrors.next('errors.errorOccurred');
        } finally {
            this.processing = false;
        }
    }

    protected readonly baseExportFontSizeEnumOptions =
        baseExportFontSizeEnumOptions;
}
